import { type GetListingQuery, R2SPlacement } from '@kijiji/generated/graphql-types'
import { getImageBySize } from '@kijiji/image'
import useEmblaCarousel from 'embla-carousel-react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { useTranslation } from 'next-i18next'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import { CarouselBtnVariant } from '@/components/shared/carousel-buttons/CarouselButtons'
import { ClientRender } from '@/components/shared/client-render'
import { ImageCustom } from '@/components/shared/image/ImageCustom'
import { ImageGalleryPictureSlide } from '@/components/shared/image-gallery-modal/slides/ImageGalleryPictureSlide'
import { ImageGalleryVirtualSlide } from '@/components/shared/image-gallery-modal/slides/ImageGalleryVirtualSlide'
import { ImageGalleryYoutubeSlide } from '@/components/shared/image-gallery-modal/slides/ImageGalleryYoutubeSlide'
import {
  GalleryImagesSection,
  GalleryMainImageSection,
  GalleryR2SSection,
  GalleryThumbnailSlide,
  GalleryThumbnailSlides,
  GalleryThumbnailsSection,
  ImageGalleryLayout,
  ImageGalleryMainCarousel,
  ImageGalleryNextButton,
  ImageGalleryPrevButton,
} from '@/components/shared/image-gallery-modal/styled'
import { VipSideProfileActionButtons } from '@/components/vip/vip-seller-profile/VipSideProfileActionButtons'
import { VipStickyBottom } from '@/components/vip/vip-sticky-components/VipStickyBottom'
import { GptImageGalleryOverlay } from '@/components-page/vip/advertisement/GptImageGalleryOverlay'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { R2S } from '@/features/listing/components/reply-to-seller/R2S'
import { type GalleryContent } from '@/features/listing-gallery/utils/getGalleryContent'
import { MODAL_REGISTRY } from '@/features/modal/constants/modalRegistry'
import { useModal } from '@/features/modal/hooks/useModal'
import { Spacing } from '@/ui/atoms/spacing'
import { Modal } from '@/ui/molecules/modal/Modal'
import { getYouTubeEmbedUrl } from '@/utils/url'

export type ImageGalleryModalProps = {
  handleCloseModal: (event: React.MouseEvent | React.KeyboardEvent) => void
  galleryContent: GalleryContent[]
  /**
   * Slide to start scrolling
   * */
  startIndex?: number
  /**
   * The listing object.
   */
  listing: NonNullable<GetListingQuery['listing']>

  isGSITrackingEnabled?: boolean
}

export const ImageGalleryModal: FC<ImageGalleryModalProps> = ({
  galleryContent,
  handleCloseModal,
  isGSITrackingEnabled,
  listing,
  startIndex = 0,
}) => {
  const { isOpen } = useModal(MODAL_REGISTRY.IMAGE_GALLERY)
  const { t } = useTranslation([TRANSLATION_KEYS.COMMON, TRANSLATION_KEYS.VIP])
  const { spacing } = useTheme()

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [lastImageLoaded, setLastImageLoaded] = useState(-1)

  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({ startIndex }, [])
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({ dragFree: true }, [
    WheelGesturesPlugin(),
  ])
  const galleryTitle = useMemo(
    () => `${t('vip:gallery_label')}: ${selectedIndex + 1}/${galleryContent.length}`,
    [t, selectedIndex, galleryContent.length]
  )

  const onPrevButtonClick = useCallback(() => {
    if (!emblaMainApi) return

    emblaMainApi.scrollPrev(true)
  }, [emblaMainApi])

  const onNextButtonClick = useCallback(() => {
    if (!emblaMainApi) return

    emblaMainApi.scrollNext(true)
  }, [emblaMainApi])

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return

      emblaMainApi.scrollTo(index, true)
    },
    [emblaMainApi, emblaThumbsApi]
  )

  /** Add keypress support of the carousel to move right and left */
  useEffect(() => {
    if (!emblaMainApi) return

    const handleKeydown = (event: KeyboardEvent): void => {
      switch (event.key) {
        case 'ArrowLeft':
          onPrevButtonClick()
          break
        case 'ArrowRight':
          onNextButtonClick()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [emblaMainApi, onNextButtonClick, onPrevButtonClick])

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return

    // Had to add this for our Jest tests, strange
    const selectedIndex = emblaMainApi.selectedScrollSnap()
    if (selectedIndex === undefined) return

    setSelectedIndex(emblaMainApi.selectedScrollSnap())
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap(), true)
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex])

  useEffect(() => {
    if (!emblaMainApi) return
    onSelect()

    emblaMainApi.on('select', () => onSelect()).on('reInit', () => onSelect())
  }, [emblaMainApi, onSelect])

  // function that will return the right react element based on the type of the content
  const renderGalleryContent = (content: GalleryContent, index: number) => {
    const key = `image-gallery-main-${index}`
    const dataTestId = `${key}${index === startIndex ? '-initial' : ''}`
    const inView = selectedIndex === index

    /**
     * If the selected image is loaded, then the images that are 1 before and 3 after the selected
     * image will start preloading in the background.
     */
    const selectedImageLoaded = lastImageLoaded === selectedIndex
    const shouldShowFullSize =
      selectedImageLoaded && index - selectedIndex >= -1 && index - selectedIndex <= 3

    switch (content.type) {
      case 'picture':
        return (
          <ImageGalleryPictureSlide
            alt=""
            id={key}
            key={key}
            src={content.source}
            data-testid={dataTestId}
            inView={inView}
            shouldShowFullSize={shouldShowFullSize}
            onLoadAndInView={() => setLastImageLoaded(index)}
          />
        )
      case 'youtube':
        return (
          <ImageGalleryYoutubeSlide
            key={key}
            src={getYouTubeEmbedUrl(content.videoId)}
            title={t('vip:gallery_label.youtube')}
            allow="autoplay"
            allowFullScreen
            data-testid={dataTestId}
            style={{ width: '100%', height: '100%' }}
            inView={inView}
          />
        )
      case 'virtual-tour':
        return (
          <ImageGalleryVirtualSlide
            key={key}
            data-testid={dataTestId}
            url={content.url}
            inView={inView}
          />
        )
    }
  }

  if (!isOpen) return

  return (
    <Modal
      allowPinchZoom
      borderRadius="0"
      closeButtonLabel={t('common:modals.close.button.label')}
      data-testid={MODAL_REGISTRY.IMAGE_GALLERY}
      hasCloseButton
      height={{ small: '100%', medium: '100vh' }}
      id={MODAL_REGISTRY.IMAGE_GALLERY}
      isOpen={isOpen}
      label={t('vip:gallery_label')}
      margin="0"
      onCancel={handleCloseModal}
      title={galleryTitle}
      width={{ small: '100%', medium: '100vw' }}
    >
      <ClientRender>
        <Spacing pBottom={spacing.default}>
          <GptImageGalleryOverlay />
        </Spacing>
      </ClientRender>

      <ImageGalleryLayout>
        <GalleryImagesSection>
          <GalleryMainImageSection>
            <ImageGalleryPrevButton
              enabled
              onClick={onPrevButtonClick}
              variant={CarouselBtnVariant.GALLERY}
            />

            <ImageGalleryMainCarousel ref={emblaMainRef}>
              <div>{galleryContent.map((item, index) => renderGalleryContent(item, index))}</div>
            </ImageGalleryMainCarousel>

            <ImageGalleryNextButton
              enabled
              onClick={onNextButtonClick}
              variant={CarouselBtnVariant.GALLERY}
            />
          </GalleryMainImageSection>

          <GalleryThumbnailsSection>
            <div ref={emblaThumbsRef}>
              <GalleryThumbnailSlides>
                {galleryContent.map((item, index) => (
                  <GalleryThumbnailSlide
                    selected={index === selectedIndex}
                    key={`image-gallery-thumb-${index}`}
                    onClick={() => onThumbClick(index)}
                    type="button"
                  >
                    <ImageCustom
                      alt=""
                      skipNextImage
                      srcImage={{
                        src:
                          item.type === 'picture'
                            ? getImageBySize(item.source, 140, 'WEBP', true)
                            : item.thumbnailUrl,
                      }}
                    />
                  </GalleryThumbnailSlide>
                ))}
              </GalleryThumbnailSlides>
            </div>
          </GalleryThumbnailsSection>
        </GalleryImagesSection>

        <GalleryR2SSection>
          <VipSideProfileActionButtons
            listing={listing}
            isGSITrackingEnabled={isGSITrackingEnabled}
            loadedFromGallery
          />

          <R2S isModal={true} listing={listing} trackingPlacement={R2SPlacement.Gallery} />
        </GalleryR2SSection>
      </ImageGalleryLayout>

      <VipStickyBottom
        listing={listing}
        r2sPlacement={R2SPlacement.Gallery}
        isGSITrackingEnabled={isGSITrackingEnabled}
      />
    </Modal>
  )
}
